import React, { createContext, useContext, useState, useCallback } from 'react';
import { AppManifest, SlotRegistration } from '../modules/module-types';

interface ModuleContextValue {
  installedModules: AppManifest[];
  enabledModuleIds: string[];
  slots: Map<string, SlotRegistration[]>;
  getComponentsForSlot: (slotId: string) => SlotRegistration[];
  registerSlotComponent: (reg: SlotRegistration) => void;
  unregisterSlotComponent: (moduleId: string, slotId?: string) => void;
  enableModule: (moduleId: string) => void;
  disableModule: (moduleId: string) => void;
}

const ModuleContext = createContext<ModuleContextValue | null>(null);

export const ModuleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [installedModules, setInstalledModules] = useState<AppManifest[]>([]);
  const [enabledModuleIds, setEnabledModuleIds] = useState<string[]>([]);
  const [slotsMap, setSlotsMap] = useState<Map<string, SlotRegistration[]>>(new Map());

  const registerSlotComponent = useCallback((reg: SlotRegistration) => {
    setSlotsMap((prev) => {
      const next = new Map(prev);
      const existing = next.get(reg.slotId) || [];
      next.set(reg.slotId, [...existing.filter((r) => r.moduleId !== reg.moduleId), reg]);
      return next;
    });
  }, []);

  const unregisterSlotComponent = useCallback((moduleId: string, slotId?: string) => {
    setSlotsMap((prev) => {
      const next = new Map(prev);
      if (slotId) {
        const existing = next.get(slotId) || [];
        next.set(slotId, existing.filter((r) => r.moduleId !== moduleId));
      } else {
        next.forEach((list, key) => {
          next.set(key, list.filter((r) => r.moduleId !== moduleId));
        });
      }
      return next;
    });
  }, []);

  const getComponentsForSlot = useCallback((slotId: string): SlotRegistration[] => {
    return slotsMap.get(slotId) || [];
  }, [slotsMap]);

  const enableModule = (moduleId: string) => {
    setEnabledModuleIds((prev) => Array.from(new Set([...prev, moduleId])));
  };

  const disableModule = (moduleId: string) => {
    setEnabledModuleIds((prev) => prev.filter((id) => id !== moduleId));
  };

  return (
    <ModuleContext.Provider
      value={{
        installedModules,
        enabledModuleIds,
        slots: slotsMap,
        getComponentsForSlot,
        registerSlotComponent,
        unregisterSlotComponent,
        enableModule,
        disableModule,
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
};

export const useModules = (): ModuleContextValue => {
  const ctx = useContext(ModuleContext);
  if (!ctx) {
    return {
      installedModules: [],
      enabledModuleIds: [],
      slots: new Map(),
      getComponentsForSlot: () => [],
      registerSlotComponent: () => {},
      unregisterSlotComponent: () => {},
      enableModule: () => {},
      disableModule: () => {},
    };
  }
  return ctx;
};
